import { useMutation } from '@apollo/client';
import type {
  UpdateThirdPartyInput,
  UpdateThirdPartyMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateThirdPartyDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import {
  toCriticality,
  toThirdPartyStatus,
  toThirdPartyType,
} from './thirdPartyEnums';
import { useUpdateThirdPartyTRPC } from './useUpdateThirdPartyTRPC';

/**
 * Public input for the wrapper. Matches the third-party form call site where
 * Type/Status are plain strings (the select options ARE the enum values) and
 * Criticality is a coerced number. Everything else mirrors the GraphQL
 * UpdateThirdPartyInput (required id arrays + Id). The wrapper narrows
 * Type/Status/Criticality to the strict tRPC input shape via runtime parse
 * (no casts).
 */
type UpdateThirdPartyInputShape = UpdateThirdPartyInput;

export const useUpdateThirdParty = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation
  const [updateThirdPartyGraphQL, graphqlState] = useMutation(
    UpdateThirdPartyDocument,
    {
      update: (cache) => {
        evictField(cache, 'third_party');
        evictField(cache, 'third_party_by_pk');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateThirdPartyTRPC();

  const updateThirdParty = async (
    variables: UpdateThirdPartyInputShape
  ): Promise<UpdateThirdPartyMutation> => {
    if (trpcEnabled) {
      return trpcMutation.updateThirdParty({
        ...variables,
        Type: toThirdPartyType(variables.Type),
        Status: toThirdPartyStatus(variables.Status),
        Criticality: toCriticality(variables.Criticality),
      });
    }

    const result = await updateThirdPartyGraphQL({
      variables: { object: variables },
    });
    if (!result.data) {
      throw new Error('Failed to update third party');
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateThirdParty,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateThirdParty,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
